/**
 * Integrations — Personal app integrations for the signed-in user.
 *
 * Phase 1: Calendar sync (Google Calendar + Outlook). Opt-in, employee-driven.
 *   - Read free/busy (interview scheduling avoids clashes)
 *   - Create events with Google Meet / Teams links
 *
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS,
 * THIS BREAKS THE AUTH.
 */
import { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import AppShell from "../components/AppShell";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { GoogleLogo, MicrosoftOutlookLogo, Plug, PlugsConnected, CheckCircle, Warning } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function IntegrationsPage() {
  const [state, setState] = useState({ loading: true });

  const load = async () => {
    try {
      const r = await api.get("/integrations/me");
      setState({ loading: false, ...r.data });
    } catch (e) {
      toast.error(formatApiError(e));
      setState({ loading: false, error: true });
    }
  };

  useEffect(() => { load(); }, []);

  const connect = async (provider) => {
    try {
      const r = await api.get(`/integrations/${provider}/connect`);
      // Mark the upcoming callback as a CALENDAR opt-in (not sign-in) so the
      // /auth/google or /auth/microsoft/callback handler routes the code to
      // the right exchange endpoint.
      sessionStorage.setItem("sso_purpose", "calendar");
      sessionStorage.setItem("sso_provider", provider);
      // Persist the state so the callback can verify it
      try {
        const u = new URL(r.data.authorize_url);
        const st = u.searchParams.get("state");
        if (st) sessionStorage.setItem("sso_state", st);
      } catch { /* ignore */ }
      window.location.href = r.data.authorize_url;
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const disconnect = async (provider) => {
    if (!window.confirm(`Disconnect ${provider} calendar?`)) return;
    try {
      await api.post(`/integrations/${provider}/disconnect`);
      toast.success(`${provider} disconnected`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  if (state.loading) return <AppShell title="Integrations"><Card className="p-6 text-sm text-zinc-500">Loading…</Card></AppShell>;

  const g = state.integrations?.find((x) => x.provider === "google");
  const m = state.integrations?.find((x) => x.provider === "microsoft");
  const googleAvail = state.providers_available?.google;
  const msAvail = state.providers_available?.microsoft;

  return (
    <AppShell title="Integrations">
      <div className="max-w-4xl mx-auto" data-testid="integrations-page">
        <div className="mb-6">
          <h1 className="text-2xl font-semibold text-zinc-900">My Integrations</h1>
          <p className="text-sm text-zinc-600 mt-1">
            Optional connections that improve your SaarOne experience. All data flows are read or written only on your behalf.
          </p>
        </div>

        <div className="space-y-4">
          <ProviderCard
            name="Google Calendar"
            description="Sync your free/busy with the interview scheduler. Create events with Google Meet links automatically."
            icon={<GoogleLogo size={28} weight="duotone" className="text-zinc-700" />}
            connected={!!g}
            available={googleAvail}
            providerKey="google"
            integ={g}
            onConnect={() => connect("google")}
            onDisconnect={() => disconnect("google")}
          />
          <ProviderCard
            name="Microsoft Outlook"
            description="Sync your Outlook calendar — free/busy lookups and event creation with Teams meeting links."
            icon={<MicrosoftOutlookLogo size={28} weight="duotone" className="text-zinc-700" />}
            connected={!!m}
            available={msAvail}
            providerKey="microsoft"
            integ={m}
            onConnect={() => connect("microsoft")}
            onDisconnect={() => disconnect("microsoft")}
          />
        </div>

        <Card className="p-4 mt-6 bg-zinc-50 border-zinc-200">
          <div className="flex items-start gap-3">
            <Warning size={18} className="text-amber-600 mt-0.5" />
            <div className="text-xs text-zinc-600">
              <strong>Your privacy:</strong> we only access the scopes you grant. You can revoke
              access anytime here or via your Google / Microsoft account settings. We never read
              event details — only busy time blocks and the events you create through SaarOne.
            </div>
          </div>
        </Card>
      </div>
    </AppShell>
  );
}

function ProviderCard({ name, description, icon, connected, available, providerKey, integ, onConnect, onDisconnect }) {
  return (
    <Card className="p-5" data-testid={`provider-card-${providerKey}`}>
      <div className="flex items-start gap-4">
        <div className="w-12 h-12 rounded-lg bg-zinc-100 flex items-center justify-center flex-shrink-0">{icon}</div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="font-semibold text-zinc-900">{name}</h3>
            {connected ? (
              <Badge className="bg-emerald-600 hover:bg-emerald-600" data-testid={`status-${providerKey}-connected`}>
                <CheckCircle size={12} weight="fill" className="mr-1" /> Connected
              </Badge>
            ) : !available ? (
              <Badge variant="outline" className="text-zinc-500" data-testid={`status-${providerKey}-unavailable`}>Coming soon</Badge>
            ) : (
              <Badge variant="outline">Not connected</Badge>
            )}
          </div>
          <p className="text-sm text-zinc-600 mt-1">{description}</p>
          {connected && (
            <div className="text-xs text-zinc-500 mt-2">
              Connected {(integ?.connected_at || "").slice(0, 16).replace("T", " ")}
              {integ?.scope && <span> · scopes: {integ.scope.split(" ").filter((s) => !["openid","profile","email","offline_access"].includes(s)).join(", ")}</span>}
            </div>
          )}
        </div>
        <div className="flex-shrink-0">
          {connected ? (
            <Button variant="outline" onClick={onDisconnect} data-testid={`disconnect-${providerKey}-btn`}>
              <Plug size={16} className="mr-1" /> Disconnect
            </Button>
          ) : available ? (
            <Button onClick={onConnect} data-testid={`connect-${providerKey}-btn`}>
              <PlugsConnected size={16} className="mr-1" /> Connect
            </Button>
          ) : (
            <Button disabled variant="outline" data-testid={`unavailable-${providerKey}-btn`}>Not configured</Button>
          )}
        </div>
      </div>
    </Card>
  );
}
